import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Clock, Ticket, Loader2, AlertCircle, ArrowLeft, CreditCard, ShieldCheck } from 'lucide-react';
import { AgeTermsCheckbox } from '../components/AgeTermsCheckbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { reservation, raffle } = location.state || {};
  
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [checkoutAgreed, setCheckoutAgreed] = useState(false);

  useEffect(() => {
    if (!reservation || !raffle) {
      navigate('/');
      return;
    }

    const expiresAt = new Date(reservation.expires_at);
    const updateTimer = () => {
      const now = new Date();
      const diff = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setTimeLeft(diff);
      
      if (diff === 0) {
        setError('Your reservation has expired. Please try again.');
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [reservation, raffle, navigate]);

  const handleStripeCheckout = async () => {
    setProcessing(true);
    setError('');

    try {
      const ticketIds = reservation.tickets.map(t => t.ticket_id);
      const response = await axios.post(`${API_URL}/api/payments/checkout`, {
        raffle_id: raffle.raffle_id,
        ticket_ids: ticketIds,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create payment session');
      setProcessing(false);
    }
  };

  if (!reservation || !raffle) {
    return null;
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const ticketNumbers = reservation.tickets.map(t => t.ticket_number).sort((a, b) => a - b);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <Link to="/" className="flex items-center gap-2 mx-auto">
              <img src="/logo.png" alt="Boss Win" className="h-20 w-auto" />
              
            </Link>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32 md:pb-8">
        {/* Timer Warning */}
        {timeLeft > 0 && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            timeLeft < 120 ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <Clock className={`w-5 h-5 ${timeLeft < 120 ? 'text-red-500' : 'text-yellow-600'}`} />
            <span className={timeLeft < 120 ? 'text-red-600' : 'text-yellow-700'}>
              Reservation expires in <strong>{formatTime(timeLeft)}</strong>
            </span>
          </div>
        )}

        <h1 className="font-heading text-3xl font-black text-gray-900 mb-8" data-testid="checkout-title">
          Complete Payment
        </h1>

        {/* Order Summary */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Free Draw</span>
              <span className="text-gray-900 font-medium">{raffle.title}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Tickets</span>
              <span className="text-gray-900 font-medium">{reservation.tickets.length} x £{raffle.ticket_price}</span>
            </div>
            <div className="border-t border-gray-200 pt-3 flex justify-between">
              <span className="text-gray-900 font-bold">Total</span>
              <span className="text-[#EAB308] font-bold text-xl" data-testid="total-amount">£{reservation.total_amount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Ticket Numbers */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Ticket className="w-5 h-5 text-[#EAB308]" />
            Your Reserved Tickets
          </h2>
          
          <div className="flex flex-wrap gap-2" data-testid="ticket-numbers">
            {ticketNumbers.map(num => (
              <span 
                key={num}
                className="bg-yellow-50 border border-[#EAB308]/30 text-[#EAB308] px-3 py-1 rounded-lg font-mono font-bold"
              >
                #{num}
              </span>
            ))}
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#EAB308]" />
            Payment
          </h2>
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
            <ShieldCheck className="w-6 h-6 text-green-500" />
            <div>
              <p className="text-gray-900 font-medium">Secure Checkout</p>
              <p className="text-gray-500 text-sm">Apple Pay, Google Pay, or Card</p>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Age & Terms Confirmation */}
        <div className="mb-6">
          <AgeTermsCheckbox checked={checkoutAgreed} onChange={setCheckoutAgreed} />
        </div>

        {/* Pay Button - Desktop */}
        <button
          onClick={handleStripeCheckout}
          disabled={processing || timeLeft === 0 || !checkoutAgreed}
          className="hidden md:flex w-full bg-[#EAB308] text-white font-bold py-4 rounded-xl hover:bg-[#CA8A04] transition-colors disabled:opacity-50 disabled:cursor-not-allowed items-center justify-center gap-2 text-lg shadow-lg shadow-[#EAB308]/25"
          data-testid="pay-button"
        >
          {processing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Redirecting to payment...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Pay £{reservation.total_amount.toFixed(2)}
            </>
          )}
        </button>
      </main>

      {/* Mobile Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-xl border-t border-gray-200 z-50 md:hidden safe-bottom">
        <button
          onClick={handleStripeCheckout}
          disabled={processing || timeLeft === 0 || !checkoutAgreed}
          className="w-full bg-[#EAB308] text-white font-bold py-4 rounded-xl hover:bg-[#CA8A04] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
          data-testid="mobile-pay-button"
        >
          {processing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Redirecting...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Pay £{reservation.total_amount.toFixed(2)}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
